import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { clearAllCookies, getCookie } from '../helpers/cookieFunctions';
import { CRMContext } from '../App';
import axiosInstance from '../helpers/axiosInstance';
import { getDeviceId } from '../helpers/device';

interface RouteAuthGuardProps {
  component: ReactNode;
  redirect: string;
  allowedRoles: string[]; // Array of allowed roles
}

const RouteAuthGuard: React.FC<RouteAuthGuardProps> = ({
  component,
  redirect,
  allowedRoles,
}) => {
  const location = useLocation();
  const { rawId, id } = useContext(CRMContext);
  const navigate = useNavigate();
  const [deviceStatus, setDeviceStatus] = useState<string>();

  useEffect(() => {
    if (!id) return;
    const checkDeviceStatus = async () => {
      try {
        const res = await axiosInstance.get(`/devices/status/${id}`, {
          params: { deviceId: await getDeviceId() },
        });
        const status = res.data.status;

        setDeviceStatus(status);
      } catch (err) {
        setDeviceStatus('approved');
      }
    };

    if (getCookie('peliswan_crm_token')) {
      checkDeviceStatus();
    }
  }, [id, location.pathname]);

  useEffect(() => {
    if (deviceStatus === 'rejected') {
      clearAllCookies();
      navigate('/auth/signin', { replace: true });
    }
  }, [deviceStatus, navigate]);

  const hasAccess = () => {
    // Check if token is present when allowedRoles is empty or user's role is included in allowedRoles
    return allowedRoles.length === 0 || allowedRoles.includes(rawId);
  };

  if (!getCookie('peliswan_crm_token'))
    return (
      <Navigate to={redirect} state={{ from: location }} replace={false} />
    );

  if (
    deviceStatus === 'pending' &&
    location.pathname !== '/device-pending'
  ) {
    return <Navigate to="/device-pending" replace={true} />;
  }
  if (!hasAccess()) {
    return (
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex md:justify-between items-center gap-3">
          <span
            className="inline-block w-6 h-6 cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <svg
              className="h-6 w-6 dark:text-primary text-black"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16l-4-4m0 0l4-4m-4 4h18"
              />
            </svg>
          </span>
          <h2 className="text-sm md:text-title-md2 font-semibold text-black dark:text-white">
            <p className="text-danger">
              You do not have permission to access this page. Permission Denied.
            </p>
          </h2>
        </div>
      </div>
    );
  }

  return <>{component}</>;
};

export default RouteAuthGuard;
